import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Plus, FileUp, Trash2, Compass, Loader2 } from "lucide-react";
import { useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { Button } from "@/components/ui/button";
import { AddPaperDialog } from "@/components/papers/AddPaperDialog";
import { ImportPapersDialog } from "@/components/papers/ImportPapersDialog";
import { StartOverDialog } from "@/components/concepts/StartOverDialog";
import { useConcept } from "@/hooks/useConcepts";
import { useRemovePaper } from "@/hooks/usePapers";
import type { PaperWithRole } from "@/types/paper";
import type { FacetSummary } from "@/types/concept";
import { TermHint } from "@/components/ui/term-hint";

interface Props {
  papers: PaperWithRole[];
  selectedPaperId?: string;
  onSelectPaper: (paper: PaperWithRole) => void;
  conceptId: string;
  hasTrawledPapers: boolean;
  /** force=true runs a fresh discovery; omit/false returns the saved set. */
  onDiscover?: (force?: boolean) => void;
  /** When true, suppress the empty state (used when CorpusSeedingPanel is shown instead). */
  hideEmptyState?: boolean;
  /** The seed list failed to load: show an error with retry, not "No seed papers yet". */
  loadFailed?: boolean;
  onRetry?: () => void;
  /** Active angles (2+ only); groups the expanded list by angle. */
  angles?: FacetSummary[];
}

function chipLabel(paper: PaperWithRole): string {
  const authors = paper.authors ?? [];
  const firstAuthor =
    authors.length > 0
      ? authors[0].split(",")[0].split(" ").pop() ?? authors[0]
      : paper.title;
  const year = paper.year ?? "?";
  return `${firstAuthor} ${year}`;
}

function truncateAuthors(authors: string[]): string {
  if (!authors?.length) return "";
  const shown = authors.slice(0, 3).join(", ");
  return authors.length > 3 ? `${shown} et al.` : shown;
}

export function SeedPapersSection({
  papers,
  selectedPaperId,
  onSelectPaper,
  conceptId,
  hasTrawledPapers,
  onDiscover,
  hideEmptyState,
  loadFailed,
  onRetry,
  angles,
}: Props) {
  const [expanded, setExpanded] = useState(!hasTrawledPapers);
  const [userToggled, setUserToggled] = useState(false);
  const prevHasTrawled = useRef(hasTrawledPapers);

  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showClear, setShowClear] = useState(false);
  // Clearing seeds is a reversible reset (scope "seeds"), ring-gated server-side.
  const { data: concept } = useConcept(conceptId);
  const canClearSeeds = concept?.reset_enabled ?? false;
  const removePaper = useRemovePaper(conceptId);
  const seedDiscoveryRunning = useSeedDiscoveryStore((s) => s.isRunning);

  useEffect(() => {
    if (!prevHasTrawled.current && hasTrawledPapers && !userToggled) {
      setExpanded(false);
    }
    prevHasTrawled.current = hasTrawledPapers;
  }, [hasTrawledPapers, userToggled]);

  const toggle = () => {
    setExpanded((prev) => !prev);
    setUserToggled(true);
  };

  // Show section even when empty (so users can add seeds)
  const isEmpty = papers.length === 0;

  const groups = useMemo(() => {
    if (!angles || angles.length < 2) return null;
    const out: { key: string; label: string; papers: PaperWithRole[] }[] = angles.map((a) => ({
      key: a.id,
      label: a.label,
      papers: papers.filter((p) => p.seed_facet_ids?.includes(a.id)),
    }));
    const ids = new Set(angles.map((a) => a.id));
    out.push({
      key: "__none",
      label: "No angle",
      papers: papers.filter((p) => !p.seed_facet_ids?.some((id) => ids.has(id))),
    });
    return out.filter((g) => g.papers.length > 0);
  }, [angles, papers]);

  const renderRow = (p: PaperWithRole, keyPrefix = "") => (
    <div
      key={`${keyPrefix}${p.id}`}
      className="group flex items-start gap-1 transition-colors hover:bg-white/5"
      style={{
        borderLeft:
          selectedPaperId === p.id
            ? "3px solid #89b4fa"
            : "3px solid transparent",
      }}
    >
      <button
        onClick={() => onSelectPaper(p)}
        className="block min-w-0 flex-1 cursor-pointer px-3 py-1.5 text-left"
      >
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-medium text-foreground">
            {p.title}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {truncateAuthors(p.authors ?? [])}
            {p.year ? `, ${p.year}` : ""}
          </span>
        </div>
        {/* relevance_note is generated by a background LLM job after
            add (abstract backfill → generate_annotations), so it's null
            for a while. Seed-discovery papers carry their own rationale
            synchronously — show it immediately; the richer LLM note
            takes over once it lands. */}
        {(p.relevance_note || p.seed_discovery_metadata?.reason) && (
          <div
            className="mt-0.5 text-[11px] italic"
            style={{ color: "#89b4fa" }}
          >
            {p.relevance_note || p.seed_discovery_metadata?.reason}
          </div>
        )}
        {groups && (p.seed_facet_ids?.length ?? 0) > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {(angles ?? [])
              .filter((a) => p.seed_facet_ids?.includes(a.id))
              .map((a) => (
                <span
                  key={a.id}
                  className="rounded px-1.5 text-[10px]"
                  style={{ background: "rgba(137,180,250,0.15)", color: "#89b4fa" }}
                >
                  {a.label}
                </span>
              ))}
          </div>
        )}
      </button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="mr-1 mt-1.5 opacity-0 group-hover:opacity-100"
        title="Remove seed"
        onClick={() => removePaper.mutate(p.id)}
      >
        <Trash2 className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );

  return (
    <div className="rounded-lg" style={{ background: "#2a2a3e" }} data-tour-target="seed-papers-panel">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          onClick={toggle}
          className="flex items-center gap-2 text-sm font-semibold text-foreground"
        >
          {expanded ? (
            <ChevronDown className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 text-muted-foreground" />
          )}
          Seed Papers <TermHint term="seed" />
          <span
            className="text-xs font-normal"
            style={{
              background: "rgba(137,180,250,0.15)",
              color: "#89b4fa",
              padding: "1px 6px",
              borderRadius: 10,
            }}
          >
            {papers.length}
          </span>
        </button>
        {expanded && (
          <div className="flex items-center gap-1">
            {papers.length > 0 && canClearSeeds && (
              <Button
                variant="ghost"
                size="icon-sm"
                title="Clear all seeds"
                onClick={() => setShowClear(true)}
              >
                <Trash2 className="size-3.5 text-muted-foreground" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              title="Import from file"
              onClick={() => setShowImport(true)}
            >
              <FileUp className="size-3.5 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Add paper"
              onClick={() => setShowAdd(true)}
            >
              <Plus className="size-3.5 text-muted-foreground" />
            </Button>
          </div>
        )}
      </div>

      {!expanded && !isEmpty && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2 pl-8">
          {papers.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelectPaper(p)}
              className="cursor-pointer rounded-md px-2 py-0.5 text-xs transition-colors"
              style={{
                background: "rgba(137,180,250,0.1)",
                borderWidth: 1,
                borderStyle: "solid",
                borderColor:
                  selectedPaperId === p.id
                    ? "rgba(137,180,250,0.4)"
                    : "rgba(137,180,250,0.15)",
                color: "#89b4fa",
              }}
            >
              {chipLabel(p)}
            </button>
          ))}
        </div>
      )}

      {expanded && isEmpty && loadFailed && (
        <div className="px-3 pb-3 text-center">
          <p className="mb-2 text-xs text-muted-foreground">
            Couldn't load your seed papers. Check your connection and try again.
          </p>
          {onRetry && (
            <Button size="xs" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      )}

      {expanded && isEmpty && !loadFailed && !hideEmptyState && (
        <div className="px-3 pb-3 text-center">
          <p className="mb-2 text-xs text-muted-foreground">
            No seed papers yet. Add papers to anchor your literature search.
          </p>
          <div className="flex justify-center gap-2">
            {onDiscover && (
              <Button
                size="xs"
                variant="default"
                onClick={() => onDiscover()}
                disabled={seedDiscoveryRunning}
                className="min-w-[8rem] whitespace-nowrap"
              >
                {seedDiscoveryRunning ? <Loader2 className="size-3 animate-spin" /> : <Compass className="size-3" />}
                {seedDiscoveryRunning ? "Discovering..." : "Discover Seeds"}
              </Button>
            )}
            <Button size="xs" variant="outline" onClick={() => setShowAdd(true)}>
              <Plus className="size-3" />
              Add Paper
            </Button>
            <Button size="xs" variant="outline" onClick={() => setShowImport(true)}>
              <FileUp className="size-3" />
              Import
            </Button>
          </div>
        </div>
      )}

      {expanded && !isEmpty && (
        <div className="pb-1">
          {groups
            ? groups.map((g) => (
                <div key={g.key}>
                  <div className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {`${g.label} (${g.papers.length})`}
                  </div>
                  {g.papers.map((p) => renderRow(p, g.key))}
                </div>
              ))
            : papers.map((p) => renderRow(p))}
          {onDiscover && (
            <div className="flex justify-center px-3 pt-2">
              <Button
                size="xs"
                variant="outline"
                onClick={() => onDiscover(true)}
                disabled={seedDiscoveryRunning}
                className="gap-1.5 whitespace-nowrap"
                title="Find a fresh set of seed suggestions (replaces the current candidates)"
              >
                {seedDiscoveryRunning ? <Loader2 className="size-3 animate-spin" /> : <Compass className="size-3" />}
                {seedDiscoveryRunning ? "Re-discovering…" : "Re-discover seeds"}
              </Button>
            </div>
          )}
        </div>
      )}

      <AddPaperDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        conceptId={conceptId}
        role="foundational"
      />

      <ImportPapersDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        conceptId={conceptId}
      />

      <StartOverDialog
        open={showClear}
        onClose={() => setShowClear(false)}
        conceptId={conceptId}
        scope="seeds"
      />
    </div>
  );
}
